import { useEffect } from "react";

import { Controller, useWatch } from "react-hook-form";
import { useFormCompat } from "@/hooks/useFormCompat";
import Select, { ActionMeta, MultiValue } from "react-select";

import Button from "@components/Button";
import { Input } from "@components/Field";
import Modal, { ModalFooter, ModalHeader, ModalTitle } from "@components/Modal";

// Type definitions
interface User {
  id: number;
  email: string;
}

interface Subscriber {
  id: number;
  email: string;
}

interface SubscriberOption {
  id: number;
  value: number;
  label: string;
  email: string;
  key: string;
}

type IntervalType = "daily" | "weekly" | "monthly" | "quarterly" | "yearly";
type TemplateType = "overview" | "social_media_overview" | "time_comparison";

interface ScheduleModalProps {
  show: boolean;
  onHide: () => void;
  interval: IntervalType;
  subscribers: Subscriber[];
  template: TemplateType;
  users: User[];
  setInterval: (interval: IntervalType) => void;
  setSubscribers: (subscribers: SubscriberOption[]) => void;
}

interface FormData {
  interval: IntervalType;
  subscribers: SubscriberOption[];
}

declare const L: {
  REPORTS_INTERVAL_DAILY: string;
  REPORTS_INTERVAL_WEEKLY: string;
  REPORTS_INTERVAL_MONTHLY: string;
  REPORTS_INTERVAL_QUARTERLY: string;
  REPORTS_INTERVAL_YEARLY: string;
  REPORTS_SCHEDULE_HEADER: string;
  REPORTS_SCHEDULE_INTERVAL_LABEL: string;
  REPORTS_LIST_HEADER_SUBSCRIBERS: string;
  REPORTS_SCHEDULE_RECIPIENTS_HELP_TEXT: string;
  REPORTS_CANCEL: string;
  REPORTS_SCHEDULE_SAVE_BUTTON: string;
};

export const ScheduleModal = (props: ScheduleModalProps) => {
  const { control, handleSubmit, setValue, reset } = useFormCompat<FormData>({
    defaultValues: {
      interval: props.interval,
      subscribers: [],
    },
  });

  useEffect(() => {
    const subscriberOptions: SubscriberOption[] = props.subscribers.map(
      (sub) => ({
        id: sub.id,
        value: sub.id,
        label: sub.email,
        key: sub.email,
        email: sub.email,
      })
    );

    reset({
      interval: props.interval,
      subscribers: subscriberOptions,
    });
  }, [props.subscribers, props.interval, reset]);

  const intervalTitleMapping: Record<IntervalType, string> =
    props.template === "overview" || props.template === "social_media_overview"
      ? {
          daily: L.REPORTS_INTERVAL_DAILY,
          weekly: L.REPORTS_INTERVAL_WEEKLY,
          quarterly: L.REPORTS_INTERVAL_QUARTERLY,
          yearly: L.REPORTS_INTERVAL_YEARLY,
          monthly: L.REPORTS_INTERVAL_MONTHLY, // Added for type completeness
        }
      : {
          daily: L.REPORTS_INTERVAL_DAILY,
          weekly: L.REPORTS_INTERVAL_WEEKLY,
          monthly: L.REPORTS_INTERVAL_MONTHLY,
          quarterly: L.REPORTS_INTERVAL_QUARTERLY,
          yearly: L.REPORTS_INTERVAL_YEARLY,
        };

  const subscriberOptions: SubscriberOption[] = props.users.map((user) => ({
    id: user.id,
    value: user.id,
    label: user.email,
    email: user.email,
    key: user.email,
  }));

  const handleSetSubscribers = (
    selectedOptions: MultiValue<SubscriberOption>,
    _actionMeta: ActionMeta<SubscriberOption>
  ) => {
    const subscribers = selectedOptions
      ? selectedOptions.map((option) => ({
          id: option.value,
          value: option.value,
          label: option.label,
          email: option.email,
          key: option.email,
        }))
      : [];

    setValue("subscribers", subscribers);
  };

  const onSubmit = (data: FormData) => {
    props.setInterval(data.interval);
    props.setSubscribers(data.subscribers);
    props.onHide();
  };

  const currentInterval = useWatch({ control, name: "interval" });
  const currentSubscribers = useWatch({ control, name: "subscribers" });

  return (
    <Modal show={props.show} onHide={props.onHide}>
      <form onSubmit={handleSubmit(onSubmit)} noValidate>
        <ModalHeader>
          <ModalTitle>{L.REPORTS_SCHEDULE_HEADER}</ModalTitle>
        </ModalHeader>

        <div className="mb-3">
          <label>{L.REPORTS_SCHEDULE_INTERVAL_LABEL}</label>

          <div>
            {props.template === "time_comparison" ? (
              <Input
                block
                type="text"
                value={intervalTitleMapping[currentInterval]}
                disabled
                readOnly
              />
            ) : (
              <Controller
                name="interval"
                control={control}
                render={({ field }) => (
                  <select {...field}>
                    {Object.entries(intervalTitleMapping).map(
                      ([value, label]) => (
                        <option key={value} value={value}>
                          {label}
                        </option>
                      )
                    )}
                  </select>
                )}
              />
            )}
          </div>
        </div>

        <div className="mb-3">
          <label htmlFor="subscribers">
            {L.REPORTS_LIST_HEADER_SUBSCRIBERS}
          </label>

          <div>
            <Controller
              name="subscribers"
              control={control}
              render={({ field }) => (
                <Select
                  {...field}
                  classNamePrefix="react-select"
                  isMulti
                  value={currentSubscribers}
                  onChange={handleSetSubscribers}
                  options={subscriberOptions}
                  getOptionLabel={(option: SubscriberOption) => option.label}
                  getOptionValue={(option: SubscriberOption) =>
                    option.value.toString()
                  }
                />
              )}
            />
            <div className="text-muted-foreground">
              {L.REPORTS_SCHEDULE_RECIPIENTS_HELP_TEXT}
            </div>
          </div>
        </div>

        <ModalFooter>
          <Button variant="secondary" onClick={props.onHide} type="button">
            {L.REPORTS_CANCEL}
          </Button>
          <Button type="submit">{L.REPORTS_SCHEDULE_SAVE_BUTTON}</Button>
        </ModalFooter>
      </form>
    </Modal>
  );
};
